/**
 * Textos en español (República Dominicana).
 *
 * Nada de lo que hay aquí menciona a la clienta #1 (§1.6.3): lo que sea
 * particular de una organización sale de su configuración, no de este archivo.
 */

export const es = {
  app: {
    name: 'Digital Share',
    tagline: 'Una nota. Todas tus portadas.',
    pitch:
      'Redacta una vez y publica en todos tus periódicos a la vez, cada uno con su categoría, su imagen y su titular.',
  },

  common: {
    email: 'Correo electrónico',
    password: 'Contraseña',
    name: 'Nombre',
    cancel: 'Cancelar',
    save: 'Guardar',
    delete: 'Borrar',
    loading: 'Cargando…',
    retry: 'Reintentar',
    signOut: 'Cerrar sesión',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
  },

  auth: {
    signIn: {
      title: 'Entrar',
      submit: 'Entrar',
      noAccount: '¿No tienes cuenta?',
      createOne: 'Crea una',
      failed: 'No pudimos entrar con esos datos.',
      forgot: '¿Olvidaste tu contraseña?',
      invitationOnly:
        'El acceso es por invitación. Si te invitaron, abre el enlace del correo que recibiste.',
    },
    signUp: {
      title: 'Crear cuenta',
      submit: 'Crear cuenta',
      haveAccount: '¿Ya tienes cuenta?',
      signIn: 'Entra',
      // Se explica el porqué en vez de listar reglas de composición: pedir
      // símbolos produce "Clave1!" en todas partes y no añade seguridad real.
      passwordHint: 'Mínimo 12 caracteres. Una frase larga es más segura y más fácil de recordar.',
      failed: 'No pudimos crear la cuenta.',
    },
    forgot: {
      title: 'Recuperar contraseña',
      intro: 'Escribe el correo de tu cuenta y te enviaremos un enlace para elegir una contraseña nueva.',
      submit: 'Enviar enlace',
      // Deliberadamente condicional: no confirma si la cuenta existe.
      sent: 'Si {{email}} tiene una cuenta, acabamos de enviarle un enlace.',
      sentHelp:
        'El enlace caduca en una hora y solo sirve una vez. Si no lo ves en unos minutos, revisa la carpeta de correo no deseado.',
      tooMany: 'Demasiados intentos seguidos. Espera un minuto y vuelve a probar.',
      failed: 'No pudimos enviar el correo. Inténtalo de nuevo en un momento.',
      back: 'Volver a entrar',
    },
    reset: {
      title: 'Nueva contraseña',
      newPassword: 'Contraseña nueva',
      submit: 'Guardar contraseña',
      tooShort: 'La contraseña necesita al menos {{min}} caracteres.',
      failed: 'No pudimos cambiar la contraseña. Puede que el enlace haya caducado: pide otro.',
      invalidLink: 'Este enlace caducó o ya se usó. Pide uno nuevo.',
      requestAnother: 'Pedir otro enlace',
      done: 'Contraseña cambiada. Por seguridad cerramos las sesiones que tenías abiertas.',
      goSignIn: 'Entrar',
    },
  },

  org: {
    chooser: {
      title: 'Elige una organización',
      subtitle: 'Tus datos y tus sitios pertenecen a una organización.',
      none: 'Todavía no perteneces a ninguna organización.',
      noneInvitationOnly:
        'Tu cuenta todavía no pertenece a ninguna organización. Pide a quien administra la tuya que te envíe una invitación.',
      create: 'Crear organización',
      creating: 'Creando…',
      nameLabel: 'Nombre de la organización',
      namePlaceholder: 'Periódicos del Caribe',
      enter: 'Entrar',
    },
    members: {
      title: 'Equipo',
      invite: 'Invitar',
      inviteSent: 'Invitación enviada a {{email}}.',
      roleLabel: 'Rol',
    },
    roles: {
      owner: 'Propietario',
      admin: 'Administrador',
      editor: 'Editor',
      author: 'Redactor',
      viewer: 'Lector',
    },
    roleHints: {
      owner: 'Control total, incluido eliminar la organización.',
      admin: 'Gestiona sitios, equipo y ajustes.',
      editor: 'Escribe y publica en los sitios que tenga asignados.',
      author: 'Escribe borradores y los envía a revisión. No publica.',
      viewer: 'Solo lectura.',
    },
  },

  invitation: {
    title: 'Te invitaron a una organización',
    accepting: 'Aceptando la invitación…',
    accepted: '¡Listo! Ya formas parte del equipo.',
    failed: 'No pudimos aceptar la invitación.',
    expired: 'Esta invitación caducó o ya fue usada.',
    signInFirst: 'Entra o crea tu cuenta para aceptar la invitación.',
    accept: 'Aceptar invitación',
    intro: 'Te damos la bienvenida a',
    emailHint: 'La invitación es para esta dirección y no se puede cambiar.',
    createAndEnter: 'Crear mi cuenta y entrar',
    alreadyHasAccount: 'Esa dirección ya tiene una cuenta. Entra con ella para aceptar la invitación.',
    wrongAccount:
      'Esta invitación es para {{invited}}, pero has entrado como {{current}}. Cierra sesión para aceptarla con la cuenta correcta.',
    expiredHelp:
      'Las invitaciones caducan a los 7 días y solo sirven una vez. Pide a quien te invitó que te envíe otra.',
  },

  nav: {
    dashboard: 'Panel',
    compose: 'Redactar',
    publications: 'Publicaciones',
    sites: 'Sitios',
    sections: 'Categorías',
    clients: 'Clientes',
    main: 'Navegación principal',
    suspended:
      'Esta cuenta está suspendida. Puedes entrar y ver todo, pero no se publica nada, tampoco lo programado. Habla con quien administra tu cuenta.',
    openMenu: 'Abrir el menú',
    closeMenu: 'Cerrar el menú',
  },

  sections: {
    // Se llaman "categorías" porque así las llama WordPress y así piensa en
    // ellas quien redacta. Por dentro siguen siendo "secciones": son las del
    // panel, no las de cada periódico, y esa diferencia es la que hay que
    // explicar aquí.
    title: 'Categorías',
    subtitle:
      'Estas son tus categorías, las del panel. Eliges "Política" una vez al redactar y cada periódico la publica en la categoría suya que le asignes, aunque allí se llame distinto.',
    nameLabel: 'Nombre de la categoría',
    namePlaceholder: 'Política',
    nameHint: 'Como la llamáis en la redacción.',
    slugPreview: 'Identificador: {{slug}}',
    create: 'Crear categoría',
    rename: 'Renombrar',
    empty: 'Todavía no hay categorías. Crea la primera arriba.',
    deleteConfirm:
      '¿Borrar "{{name}}"? Se perderá su correspondencia con la categoría de cada sitio, y rehacerla hay que hacerla sitio por sitio.',
  },

  mapping: {
    title: 'Dónde publica este sitio',
    open: 'Categorías',
    // "Tu categoría" frente a "Categoría en este sitio": al llamarse igual las
    // dos cosas, la columna tiene que decir de quién es cada una.
    section: 'Tu categoría',
    category: 'Categoría en este sitio',
    unmapped: '— sin asignar —',
    stale: 'la categoría ya no existe',
    noSections: 'Crea primero alguna categoría en el menú Categorías para poder asignarla aquí.',
    noCategories:
      'Todavía no hemos leído las categorías de este sitio. Pulsa "Comprobar" en la tarjeta del sitio.',
    staleWarning_one: 'Hay {{count}} categoría tuya asignada a una que ya no existe en el sitio.',
    staleWarning_other: 'Hay {{count}} categorías tuyas asignadas a otras que ya no existen en el sitio.',
    fetchedAt: 'Categorías leídas el {{time}}.',
    neverFetched: 'Todavía no se han leído las categorías de este sitio.',
    missingWarning_one:
      '{{count}} categoría sin asignar: las noticias de esa categoría no se podrán publicar aquí.',
    missingWarning_other:
      '{{count}} categorías sin asignar: las noticias de esas categorías no se podrán publicar aquí.',
  },

  sites: {
    title: 'Sitios',
    subtitle:
      'Los periódicos donde Digital Share puede publicar. Cada uno necesita el conector instalado y autorizado desde su propio wp-admin.',
    connect: 'Conectar un sitio',
    usage: '{{used}} de {{max}} periódicos de tu plan',
    limitReached:
      'Llegaste al límite de periódicos de tu plan. Para conectar más, habla con quien administra tu cuenta.',
    check: 'Comprobar',
    rotate: 'Rotar secreto',
    rotating: 'Rotando…',
    rotateConfirm:
      '¿Rotar el secreto de "{{name}}"? Se genera uno nuevo y se le entrega al sitio. Si algo falla, el actual sigue funcionando.',
    rotateOk: 'Secreto rotado. El sitio sigue conectado.',
    checking: 'Comprobando…',
    checkOk: 'Responde correctamente ({{ms}} ms).',
    disconnect: 'Desconectar',
    disconnectConfirm:
      '¿Desconectar "{{name}}"? Dejará de recibir publicaciones. Las entradas ya publicadas no se tocan.',
    altTransport: 'ruta alternativa',
    empty: {
      title: 'Todavía no hay ningún sitio conectado',
      body: 'Conecta tu primer periódico para empezar. Necesitas acceso de administrador a su WordPress.',
    },
    info: {
      timezone: 'Zona horaria',
      seo: 'SEO',
      author: 'Publica como',
    },
    wizard: {
      title: 'Conectar un sitio',
      urlLabel: 'Dirección del periódico',
      urlHint: 'La dirección principal del sitio, tal cual la escribirías en el navegador.',
      nameLabel: 'Nombre (opcional)',
      namePlaceholder: 'Se toma del dominio si lo dejas vacío',
      submit: 'Comprobar sitio',
      checking: 'Comprobando el sitio…',
      detected: 'Conector encontrado (versión {{version}}). Ya puedes autorizar la conexión.',
      step1: 'Abre el enlace de abajo: te lleva al wp-admin de ese periódico.',
      step2: 'Revisa que el panel que aparece en pantalla es este, y pulsa Autorizar.',
      step3: 'Vuelve aquí. El sitio aparecerá en la lista.',
      openAdmin: 'Abrir el wp-admin para autorizar',
      expires: 'El enlace caduca a las {{time}}.',
      done: 'Ya lo autoricé, actualizar la lista',
    },
  },

  dashboard: {
    title: 'Panel',
    compose: 'Redactar noticia',
    stats: {
      publishedToday: 'publicadas hoy',
      inFlight: 'en curso o programadas',
      sites: 'periódicos conectados',
    },
    attention: {
      title: 'Requiere tu atención',
      site: 'Sitio',
    },
    recent: {
      title: 'Últimas publicaciones',
      all: 'Ver todas',
      empty: 'Todavía no has publicado nada.',
    },
    // Sin periódicos no hay nada que resumir: se dice por dónde empezar en vez
    // de enseñar tarjetas con ceros.
    start: {
      title: 'Tres pasos para tu primera publicación',
      connect:
        'Conecta un periódico. Necesitas que su administrador instale el conector y autorice la conexión.',
      categories: 'Crea tus categorías y asígnalas a las de cada periódico.',
      write: 'Redacta la noticia, elige a qué periódicos va y publícala.',
      cta: 'Conectar un periódico',
    },
  },

  // Estados de una publicación. Viven aquí y no en cada pantalla para que el
  // panel, la lista y el detalle digan siempre lo mismo.
  publicationStatus: {
    scheduled: 'Programada',
    processing: 'En curso',
    completed: 'Completada',
    partial: 'Parcial',
    failed: 'Fallida',
    canceled: 'Cancelada',
  },

  // Administración de la plataforma: solo la ve quien la administra.
  platform: {
    title: 'Clientes',
    subtitle:
      'Cada cliente es una organización con lo que tiene contratado. Aquí los das de alta, ajustas sus límites y, si hace falta, los suspendes.',
    newClient: 'Nuevo cliente',
    newClientHelp:
      'Se crea su organización y se envía una invitación al correo que indiques. Esa persona elegirá su contraseña y será la propietaria de la cuenta.',
    createAndInvite: 'Crear e invitar',
    created: 'Cliente «{{name}}» creado. Enviamos la invitación a {{email}}.',
    resend: 'Reenviar invitación',
    resent: 'Invitación reenviada a {{email}}. El enlace anterior dejó de valer.',
    editLimits: 'Límites',
    suspend: 'Suspender',
    resume: 'Levantar suspensión',
    suspendConfirm:
      '¿Suspender a «{{name}}»? No podrá publicar, tampoco lo que tenga programado. No se borra nada y puedes levantar la suspensión cuando quieras.',
    noOwner: 'Sin propietario',
    belowUsage:
      'Ya tiene {{used}} periódicos conectados. Bajar el límite no desconecta ninguno: solo impide conectar más.',
    fields: {
      name: 'Nombre del cliente',
      namePlaceholder: 'Agencia del Este',
      ownerEmail: 'Correo de quien administrará la cuenta',
      ownerEmailHint: 'Recibirá la invitación. Caduca a los 7 días.',
      maxSites: 'Periódicos que puede conectar',
      maxMembers: 'Miembros de su equipo',
      maxMembersHint: 'Contando a quien administra la cuenta.',
    },
    status: {
      active: 'Activo',
      invited: 'Invitación enviada',
      invitationExpired: 'Invitación caducada',
      suspended: 'Suspendido',
      paused: 'En pausa por el cliente',
    },
    usage: {
      sites: 'Periódicos',
      members: 'Equipo',
      publications: 'Publicaciones, 30 días',
      lastPublication: 'Última publicación',
      never: 'Nunca',
    },
  },

  publication: {
    // Una noticia programada no está «en cola»: está esperando su hora. Con
    // «En cola» junto a cada periódico parecía atascada.
    scheduledChip: 'Programada · {{time}}',
    scheduledHelp_one:
      'Saldrá sola a esa hora en {{count}} periódico. No hace falta dejar el panel abierto, y puedes cancelarla hasta entonces.',
    scheduledHelp_other:
      'Saldrá sola a esa hora en los {{count}} periódicos. No hace falta dejar el panel abierto, y puedes cancelarla hasta entonces.',
    scheduledFor: 'programada para el {{when}}',
  },

  errors: {
    generic: 'Algo salió mal. Inténtalo de nuevo.',
    network: 'No pudimos contactar con el servidor.',
    notFound: 'No encontramos esa página.',
  },
} as const;

export type Translations = typeof es;
